/*
this is a reusable component that list the columsn with the group name
you can select a column from the list either by clicking on the column or by searching for the column
this resuable component is set with a height of 30px and a width of 100%
and all the text is styled with the listItemTextStyles, and can't change by the user from parent component
@groupList: required, the list of columns with the group name, with a format like this 
            [
                {
                    colId: "columnId",
                    groupName: "Group Name",
                    headerName: "Column Name",
                }.
                ...
            ]
@placeholder: required, the placeholder for the search input
@ascendingListByGroup: optional, default is true, if true the list will be sorted by the group name in ascending order, if false the list will be sorted by the group name in descending order
@handleSlection: required, the function that will be called when a column is selected, it will return the selected column
@disabled: optional, boolean to enable/disable the autocompolete, default value is false
*/
import React, {useState} from 'react';
import { Grid, Typography, Autocomplete, TextField } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const listItemTextStyles = {
  fontFamily: "Lato",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: "700",
  lineHeight: "normal",
}

export default function GroupAutoComplete({
    groupList,
    placeholder,
    ascendingListByGroup = true,
    disabled = false,
    handleSlection,
    styles = {},
    showEndAdornment = true,
    defaultOpen = false,
}) {
    const [isDropdownOpen, setDropdownOpen] = useState(defaultOpen); // State to track dropdown state

    return (
        <Grid 
            item 
            sx = {{
                height: styles.height || "30px",
                width: "100%",
            }}
        >
            <Autocomplete
                options={ascendingListByGroup ? groupList?.sort((a, b) => -b.groupName.localeCompare(a.groupName)) : groupList} 
                disabled = {disabled}
                groupBy={(option) => option.groupName}
                getOptionLabel={(option) => option.headerName}
                clearIcon={null} // Removes the clear icon
                open={isDropdownOpen}
                onOpen={() => setDropdownOpen(true)} // Set to open state
                onClose={() => setDropdownOpen(false)} // Set to closed state
                onChange = {(e, value) => handleSlection(e, value)} // handle the selection of the column
                sx={{ 
                    width: '100%', 
                    height: '100%',
                    '& .MuiInputBase-root': {
                        height: '100%',
                        padding: '5px 16px !important',
                    },
                    '& .MuiFormControl-root': {
                        width: '100%',
                        height: '100%',
                    },
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        border: styles.border || '1.5px solid var(--Neutrals-500, #E2E6E9)',
                        background: 'var(--Neutrals-100, #FFF)',
                        '&:hover': {
                            border: '1.5px solid var(--Neutrals-500, #E2E6E9)', // Maintain hover state consistency
                        },
                        '&.Mui-focused': {
                            border: '1.5px solid var(--Tertiary-500, #00A6FF)',
                            background: 'var(--Neutrals-100, #FFF)',
                        },
                    },
                    '& .MuiInputBase-input': {
                        color: 'var(--Blacks-500, #100C08)',
                        padding: "0 !important",
                        textAlign: 'left',
                        fontFamily: 'Lato',
                        fontSize: '14px',
                        fontStyle: 'normal',
                        fontWeight: '700',
                        lineHeight: 'normal',
                    }, 
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                        height: '100%',
                        padding: '0',
                    }
                }} 
                renderInput={(params) => (
                    <TextField 
                        {...params} 
                        placeholder={placeholder}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {showEndAdornment &&
                                        <>
                                        {isDropdownOpen ? (
                                            <KeyboardArrowUpIcon
                                                onClick={() => setDropdownOpen(false)}
                                                style={{ color: 'var(--Blacks-500, #100C08)' }}
                                            />
                                        ) : (
                                            <KeyboardArrowDownIcon
                                                onClick={() => setDropdownOpen(true)}
                                                style={{ color: 'var(--Blacks-500, #100C08)' }}
                                            />
                                        )}
                                        </>
                                    }
                                </>
                            ),
                        }}
                        sx={{
                            '& .MuiInputBase-input': {
                                ...listItemTextStyles,
                                color: "var(--Blacks-500, #100C08)",
                            },
                            '& .MuiInputBase-input::placeholder': {
                                ...listItemTextStyles,
                                color: styles.placeholder?.color || 'var(--Blacks-500, #100C08)',
                            },
                        }}
                    />
                )}
                renderGroup={(params) => (
                    <li key={params.key}>
                        <Typography
                            sx={{...listItemTextStyles, paddingLeft: "15px", color: "var(--Neutrals-700, #C6CCD2)"}}
                        >{params.group}</Typography>
                        <Typography
                            sx={{...listItemTextStyles, paddingLeft: "10px", color: "var(--Blacks-500, #100C08)"}}
                        >{params.children}</Typography>
                    </li>
                )}
            />
        </Grid>
    )
}
